import React, { useEffect, useMemo, useState } from "react";
import { Modal, InputNumber, Spin, message, Select } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { bulkUpsertGoodsInventory, GoodsInventoryEntry } from "../../../utils/api/goodsInventoryAPI";

interface RevenueType { _id: string; name: string; }
interface Location { _id: string; name: string; }

interface InventoryModalProps {
  visible: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientId: string;
  month: string;
  revenueTypes: RevenueType[];
  availableLocations: Location[];
  defaultLocationId?: string;
  existingEntries: GoodsInventoryEntry[];
}

export const InventoryModal: React.FC<InventoryModalProps> = ({
  visible, onClose, onSaved, clientId, month,
  revenueTypes, availableLocations, defaultLocationId, existingEntries,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [values, setValues] = useState<Record<string, number>>({});

  const parsedMonth = dayjs(month, "YYYY-MM");
  const monthNum = parsedMonth.month() + 1;
  const yearNum = parsedMonth.year();

  useEffect(() => {
    if (!visible) return;
    setSelectedLocationId(defaultLocationId || availableLocations[0]?._id || "");
  }, [visible, defaultLocationId, availableLocations]);

  useEffect(() => {
    if (!visible || !selectedLocationId) return;
    const init: Record<string, number> = {};
    revenueTypes.forEach((rt) => {
      const e = existingEntries.find(
        (e) => e.locationId === selectedLocationId && e.revenueTypeId === rt._id &&
          e.month === monthNum && e.year === yearNum,
      );
      init[rt._id] = e?.closingStock ?? 0;
    });
    setValues(init);
  }, [visible, selectedLocationId, revenueTypes, existingEntries, monthNum, yearNum]);

  const selectedLocationName = useMemo(
    () => availableLocations.find((l) => l._id === selectedLocationId)?.name ?? "",
    [availableLocations, selectedLocationId],
  );

  const handleSave = async () => {
    if (!clientId || !selectedLocationId || revenueTypes.length === 0) return;
    setSaving(true);
    try {
      const entries = revenueTypes.map((rt) => ({
        clientId,
        locationId: selectedLocationId,
        revenueTypeId: rt._id,
        revenueTypeName: rt.name,
        month: monthNum,
        year: yearNum,
        procurement: 0,
        closingStock: values[rt._id] ?? 0,
      }));
      await bulkUpsertGoodsInventory(entries);
      message.success(t("Goods inventory saved successfully"));
      onSaved();
      onClose();
    } catch {
      message.error(t("Failed to save goods inventory"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={saving}
      title={
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          {t("Goods Inventory")} · {parsedMonth.format("MMMM YYYY")}
        </span>
      }
      width={420}
      className="standardHeader standardFooter"
    >
      <Spin spinning={saving}>
        <div style={{ marginTop: 8 }}>
          {revenueTypes.length === 0 ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>
              {t("No revenue types configured. Add them in Settings → Daily Revenue.")}
            </p>
          ) : (
            <>
              {availableLocations.length > 1 ? (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500, marginBottom: 6 }}>{t("Location")}</div>
                  <Select
                    style={{ width: "100%" }}
                    value={selectedLocationId}
                    onChange={setSelectedLocationId}
                    options={availableLocations.map((l) => ({ label: l.name, value: l._id }))}
                    size="large"
                  />
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                  {t("Location")}: <strong style={{ color: "#111827" }}>{selectedLocationName}</strong>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {revenueTypes.map((rt) => (
                  <div key={rt._id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <span style={{ fontWeight: 500, fontSize: 14, color: "#111827" }}>{rt.name}</span>
                    <InputNumber
                      style={{ width: 170 }}
                      min={0}
                      value={values[rt._id] ?? 0}
                      onChange={(v) => setValues((prev) => ({ ...prev, [rt._id]: v ?? 0 }))}
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                      parser={(v) => Number(v?.replace(/\./g, "") ?? "0") as any}
                      addonAfter="RSD"
                      size="large"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Spin>
    </Modal>
  );
};
